/**
 * GetStartedCards — two-card chooser for the empty/onboarding dashboard.
 *
 * Both cards are equal-weight. Copy on the add card varies between the
 * empty-state ("Add your first Member") and the onboarding state
 * ("Add another Member"); pass `state` to switch.
 */

import { Link } from 'react-router-dom';
import { Card } from '@/components/Card/Card';
import { t } from '@/lib/i18n';
import styles from './GetStartedCards.module.css';

export interface GetStartedCardsProps {
  /** Picks the add-card copy: `empty` is the no-Members state, `onboarding` is after the first add. */
  state?: 'empty' | 'onboarding';
}

export function GetStartedCards({ state = 'empty' }: GetStartedCardsProps) {
  const isEmpty = state === 'empty';
  const addTitle = isEmpty ? t('Add your first Member') : t('Add another Member');
  const addBody = isEmpty
    ? t(
        'Pick a position — Marketing Writer, Bookkeeper, Researcher. They show up in your team and are ready to work.',
      )
    : t('Round out the team with another position.');
  const jobsBody = t(
    'Browse ready-to-run Jobs. Pick one, hand it off, and get the finished work back.',
  );

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t('Getting Started')}</h2>
      </div>
      <div className={styles.grid}>
        <Link to="/team/add" state={{ skipType: true }} className={styles.cardLink}>
          <Card padding="lg" className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.iconSquare} aria-hidden="true">
                +
              </span>
            </div>
            <h3 className={styles.cardTitle}>{addTitle}</h3>
            <p className={styles.cardBody}>{addBody}</p>
            <span className={styles.cardCta}>{t('Add a Member →')}</span>
          </Card>
        </Link>

        <Link to="/jobs" className={styles.cardLink}>
          <Card padding="lg" className={styles.card}>
            <div className={styles.cardHeader}>
              <span
                className={[styles.iconSquare, styles.iconSquareMuted].join(' ')}
                aria-hidden="true"
              >
                ▤
              </span>
            </div>
            <h3 className={styles.cardTitle}>{t("Pick a Job you've been meaning to get to.")}</h3>
            <p className={styles.cardBody}>{jobsBody}</p>
            <span className={styles.cardCta}>{t('Browse the catalog →')}</span>
          </Card>
        </Link>
      </div>
    </section>
  );
}
